/**
 * Pieces both record layouts draw (the header as One draws it today, and the tabbed record): a command's button and
 * the record's notices.
 */
import { Link } from "react-router";
import type {
  Command,
  RecordNotice,
} from "@shared-core/domain/object-ui/contract";
import { useObjectManifest } from "@shared-core/domain/object-ui/manifest";
import { Button } from "../../app/components/primitives/button";

export function CommandButton({
  command,
  isPrimary,
  onRun,
}: {
  command: Command;
  isPrimary: boolean;
  onRun: () => void;
}) {
  return (
    <div className="flex max-w-56 flex-col gap-0.5">
      {/* A long label is cut at the button's width; the whole of it is the tooltip. */}
      <Button
        size="sm"
        variant={isPrimary ? "default" : "outline"}
        disabled={!command.enabled}
        onClick={onRun}
        title={command.label}
        className="min-w-0 max-w-full max-sm:h-10"
      >
        <span className="truncate">{command.label}</span>
      </Button>
      {!command.enabled && command.reason && (
        <span className="text-xs text-amber-700 dark:text-amber-400">
          {command.reason}
        </span>
      )}
    </div>
  );
}

/** Facts that change what the main command does without blocking it, said above the header's buttons. */
export function RecordNotices({
  notices,
}: {
  notices: readonly RecordNotice[];
}) {
  if (notices.length === 0) return null;
  return (
    <ul aria-label="Notices" className="space-y-1 text-sm">
      {notices.map((notice) => (
        <li
          key={notice.text}
          className={
            notice.tone === "warn"
              ? "text-amber-700 dark:text-amber-400"
              : "text-muted-foreground"
          }
        >
          {notice.text}
          <NoticeLink link={notice.link} />
        </li>
      ))}
    </ul>
  );
}

/** The record a notice names, after its sentence; nothing where it names none or the record has no page here. */
function NoticeLink({ link }: { link: RecordNotice["link"] }) {
  const index = useObjectManifest();
  const href = link ? index.hrefOf(link.record) : null;
  if (!link || !href) return null;
  return (
    <>
      {" "}
      <Link
        to={href}
        className="text-primary underline-offset-2 hover:underline"
      >
        {link.label}
      </Link>
    </>
  );
}

/** The block of the record's goal or next step, said above the buttons (`headerBlockedLeadOf`, `blockedLayoutOf`). */
export function SaidBlock({
  said,
}: {
  said: { label: string | null; reason: string } | null;
}) {
  if (!said) return null;
  return (
    <p className="text-sm">
      {said.label && <span className="font-medium">{said.label} </span>}
      <span className="text-amber-700 dark:text-amber-400">{said.reason}</span>
    </p>
  );
}
